import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import OnboardingLayout from '../../components/host/OnboardingLayout';
import { YesNoQuestion, NavigationButtons, InfoCard } from '../../components/host/OnboardingComponents';
import { HelpOutline } from '@mui/icons-material';

interface PreScreeningData {
  ownsOrManagesProperty: boolean | null;
  hasPlannedSpace: boolean | null;
  hasHighSpeedInternet: boolean | null;
  acceptsNetworkPolicies: boolean | null;
}

const HostOnboardingStep1 = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<PreScreeningData>({
    ownsOrManagesProperty: null,
    hasPlannedSpace: null,
    hasHighSpeedInternet: null,
    acceptsNetworkPolicies: null,
  });

  const handleNext = () => {
    // Validate all questions are answered
    const allAnswered = Object.values(formData).every(value => value !== null);
    const allYes = Object.values(formData).every(value => value === true);
    
    if (allAnswered && allYes) {
      // Store the form data in localStorage or context
      localStorage.setItem('hostOnboardingStep1', JSON.stringify(formData));
      navigate('/host/onboarding/step-2');
    }
  };

  const canProceed = () => {
    return Object.values(formData).every(value => value !== null) &&
           Object.values(formData).every(value => value === true);
  };

  const handleTermsClick = () => {
    // Open terms and conditions in new tab
    window.open('/terms-and-conditions', '_blank');
  };

  return (
    <OnboardingLayout
      currentStep={1}
      totalSteps={4}
      stepTitle="Host Pre-Screening"
      stepSubtitle="Let's verify your eligibility to join our network"
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Questions */}
        <Box sx={{ mb: 4 }}>
          <YesNoQuestion
            title="Do You Own Or Manage Property?"
            subtitle="This is required to host Botels on our network"
            value={formData.ownsOrManagesProperty}
            onChange={(value) => setFormData(prev => ({ ...prev, ownsOrManagesProperty: value }))}
            required
          />

          <YesNoQuestion
            title="Do You Have A Planned Space To Install Your Botel (3ft X3ft)?"
            subtitle="Minimum space requirement for installation"
            value={formData.hasPlannedSpace}
            onChange={(value) => setFormData(prev => ({ ...prev, hasPlannedSpace: value }))}
            required
          />

          <YesNoQuestion
            title="Do You Have Hi-Speed Internet?"
            subtitle="Required for optimal Botel performance"
            value={formData.hasHighSpeedInternet}
            onChange={(value) => setFormData(prev => ({ ...prev, hasHighSpeedInternet: value }))}
            required
          />

          <YesNoQuestion
            title="Do You Accept Botel Network Policies?"
            subtitle={
              <Box>
                Please review our{' '}
                <Typography
                  component="span"
                  sx={{ 
                    color: '#377dff', 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    '&:hover': { color: '#2563eb' }
                  }}
                  onClick={handleTermsClick}
                >
                  terms and conditions
                </Typography>
              </Box>
            }
            value={formData.acceptsNetworkPolicies}
            onChange={(value) => setFormData(prev => ({ ...prev, acceptsNetworkPolicies: value }))}
            required
          />
        </Box>

        {/* Help Section */}
        <InfoCard
          title="Need More Info?"
          icon={<HelpOutline />}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Have questions about the requirements? Our team is here to help!
          </Typography>
          <Typography variant="body2">
            Contact us at{' '}
            <Typography
              component="span"
              sx={{ color: '#377dff', cursor: 'pointer' }}
              onClick={() => window.open('mailto:support@bellhop.com')}
            >
              support@bellhop.com
            </Typography>
            {' '}or call us at{' '}
            <Typography
              component="span"
              sx={{ color: '#377dff', cursor: 'pointer' }}
              onClick={() => window.open('tel:+1-800-BELLHOP')}
            >
              1-800-BELLHOP
            </Typography>
          </Typography>
        </InfoCard>

        {/* Navigation */}
        <NavigationButtons
          onNext={handleNext}
          nextLabel="Next"
          canProceed={canProceed()}
        />

        {/* Warning Message */}
        {Object.values(formData).some(value => value === false) && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 1,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" sx={{ color: '#ef4444' }}>
              All requirements must be met to proceed with the host onboarding process.
            </Typography>
          </Box>
        )}
      </Box>
    </OnboardingLayout>
  );
};

export default HostOnboardingStep1;
